import { DropDownFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import {
    DataOption,
    DataOptions,
    getDataOptionsFieldDefinition,
    getDataOptionValue,
} from "@store/Framework/Common/Utilities/dataOptions";
import { LiteralUnion } from "@store/Framework/Types/Common";
import { useMemo } from "react";

export type VerticalAlignmentOption = DataOption<"top" | "middle" | "bottom">;
export interface VerticalAlignmentOptionMapper {
    top: VerticalAlignmentOption;
    middle: VerticalAlignmentOption;
    bottom: VerticalAlignmentOption;
}
export type VerticalAlignmentOptions = DataOptions<VerticalAlignmentOption> & VerticalAlignmentOptionMapper;
export type VerticalAlignment = LiteralUnion<keyof VerticalAlignmentOptionMapper>;

export const verticalAlignmentOptions: VerticalAlignmentOptions = {
    top: { id: "top", displayName: "Top", isDefault: true },
    middle: { id: "middle", displayName: "Middle" },
    bottom: { id: "bottom", displayName: "Bottom" },
};

export type VerticalAlignmentFieldValue = VerticalAlignment | undefined;
export interface VerticalAlignmentFieldOptions {
    isChildDefinition?: boolean;
}

export const getVerticalAlignmentFieldData = (value: VerticalAlignmentFieldValue) => {
    if (!value) {
        return undefined;
    }

    return getDataOptionValue(verticalAlignmentOptions, value);
};

export const getVerticalAlignmentFieldDefinition = (name: string, options?: VerticalAlignmentFieldOptions) => {
    const { isChildDefinition } = options || {};

    const definition: DropDownFieldDefinition<string> = {
        ...getDataOptionsFieldDefinition(verticalAlignmentOptions),
        name,
        displayName: "Vertical Alignment",
        fieldType: !isChildDefinition ? "General" : (undefined as any),
        hideEmptyOption: true,
    };

    return definition;
};

const getVerticalAlignmentField = (name: string, options?: VerticalAlignmentFieldOptions) => {
    const parse = (value: VerticalAlignmentFieldValue) => getVerticalAlignmentFieldData(value);
    const parseToFlex = (value: VerticalAlignmentFieldValue) => {
        if (!value) {
            return undefined;
        }

        switch (value) {
            case verticalAlignmentOptions.top.id:
                return "flex-start";
            case verticalAlignmentOptions.middle.id:
                return "center";
            case verticalAlignmentOptions.bottom.id:
                return "flex-end";
            default:
                return undefined;
        }
    };

    return {
        definition: getVerticalAlignmentFieldDefinition(name, options),
        parse,
        useParse: (...parameters: Parameters<typeof parse>) => {
            return useMemo(() => parse(...parameters), parameters);
        },
        parseToFlex,
        useParseToFlex: (...parameters: Parameters<typeof parseToFlex>) => {
            return useMemo(() => parseToFlex(...parameters), parameters);
        },
    };
};

export default getVerticalAlignmentField;
